import { useCallback, useSyncExternalStore, type Dispatch, type SetStateAction } from 'react';
import { isSetStateFunction } from '../internal/util.ts';

export type Callback = () => void;

/**
 * Represents a readable/writable external store.
 */
export type ExternalStore<T> = {
    getSnapshot: () => T;
    setValue: (value: T) => void;
    subscribe: (listener: Callback) => Callback;
};

/**
 * Connects to a {@link ExternalStore} instance. The reference should be stable.
 */
export function useExternalStore<T>(store: ExternalStore<T>): [T, Dispatch<SetStateAction<T>>] {
    const setState = useCallback(
        (state: SetStateAction<T>) => {
            if (isSetStateFunction(state)) {
                store.setValue(state(store.getSnapshot()));
            } else {
                store.setValue(state);
            }
        },
        [store],
    );
    const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot);
    return [snapshot, setState];
}
